<template>
  <div class="explorer-container">
    <div class="explorer-columns">
      <div class="topics-column">
        <TopicList
          :topics="topics"
          :selected-topic-id="selectedTopicId"
          :scenario-counts="scenarioCounts"
          :canEdit="canEdit"
          :enableDragDrop="false"
          @select-topic="handleSelectTopic"
          @preview-topic="handlePreviewTopic"
          @create-topic="handleCreateTopic"
          @edit-topic="handleEditTopic"
        />
      </div>
      <div class="scenarios-column">
        <ScenarioList
          :scenarios="scenarios"
          :selected-scenario-id="selectedScenarioId"
          :canEdit="canEdit"
          :enableDragDrop="false"
          @select="handleSelectScenario"
          @create="handleCreateScenario"
        />
      </div>
      <div class="details-column">
        <TopicPreview
          v-if="selectedTopicId && !selectedScenarioId"
          :topic="selectedTopic"
          :scenarios="scenarios"
          :selected-scenario-id="null"
          :canEdit="canEdit"
          @select-scenario="handleSelectScenario"
          @edit="handleEditTopic"
        />
        <ScenarioPreview
          v-else-if="selectedScenarioId"
          :scenario="selectedScenario"
          :canEdit="canEdit"
          @edit="handleEditScenario"
          @start="handleStartScenario"
        />
      </div>
    </div>

    <!-- Topic Editor Modal -->
    <b-modal
      v-model="isTopicEditorActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      aria-role="dialog"
      aria-modal
    >
      <TopicEditor
        :topic="topicToEdit"
        @saved="handleTopicSaved"
        @close="closeTopicEditor"
      />
    </b-modal>

    <!-- Scenario Editor Modal -->
    <b-modal
      v-model="isScenarioEditorActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      aria-role="dialog"
      aria-modal
    >
      <ScenarioEditor
        :original-scenario="scenarioToEdit"
        :initial-pgn="currentPgnToEdit"
        :current-topic-id="selectedTopicId"
        @close="closeScenarioEditor"
        @saveScenarioToTopic="handleSaveScenarioToTopic"
        @updateScenario="handleUpdateScenario"
      />
    </b-modal>
  </div>
</template>

<script>
import topicScenarioEditor from "@/mixins/topicScenarioEditor";
import topicScenarioLoader from "@/mixins/topicScenarioLoader";
import { mapGetters } from "vuex";
import ScenarioPreview from "../organisms/ScenarioPreview.vue";
import ScenarioList from "../organisms/ScenarioList.vue";
import TopicList from "../organisms/TopicList.vue";
import TopicPreview from "../organisms/TopicPreview.vue";

export default {
  name: "TopicExplorer",

  mixins: [topicScenarioEditor, topicScenarioLoader],

  props: {
    loadPrivateOnly: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    TopicList,
    ScenarioList,
    TopicPreview,
    ScenarioPreview,
  },

  computed: {
    ...mapGetters(["getProfile"]),

    isAdmin() {
      return this.getProfile?.isAdmin ?? false;
    },

    canEdit() {
      return this.isAdmin;
    },

    selectedTopic() {
      return this.topics.find((t) => t.id === this.selectedTopicId) || null;
    },
  },

  // async created() {
  //   await this.loadTopics();
  // },

  methods: {
    handleStartScenario() {
      if (this.selectedScenario && this.selectedTopicId) {
        // Check if we're in teacher context
        const isTeacherContext = this.$route.meta?.teacher ?? false;
        const examplesPath = isTeacherContext
          ? "/teacher/examples"
          : "/examples";

        this.$router.push({
          path: examplesPath,
          query: {
            topicId: this.selectedTopicId,
            scenarioId: this.selectedScenarioId,
          },
        });
      }
    },
  },
};
</script>

<style scoped>
.explorer-container {
  height: calc(100vh - 100px);
  min-height: 500px;
  max-height: 1000px;
}

.explorer-columns {
  height: 100%;
  margin: 0 !important;
  display: flex;
  border: 4px solid #dbdbdb;
}

.topics-column,
.scenarios-column {
  flex: 1;
  max-width: 400px;
}

.details-column {
  flex: 1;
  position: relative;
}

/* Add subtle separation between columns */
.topics-column::after,
.scenarios-column::after {
  content: "";
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, #dbdbdb 0%, #dbdbdb 100%);
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .explorer-container {
    height: calc(100vh - 120px);
  }

  .explorer-columns {
    flex-direction: column;
  }

  .topics-column::after,
  .scenarios-column::after {
    width: 100%;
    height: 1px;
    right: 0;
    bottom: 0;
    background: linear-gradient(to right, #dbdbdb 0%, #dbdbdb 100%);
  }
}
</style>
